// Phase 2C-1 — Client Projects main page: 5 metrics, Board/List/Schedule views, filters.
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LayoutGrid, List as ListIcon, CalendarDays, Search } from "lucide-react";
import ProjectDetail from "./ProjectDetail";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const STAGES = {
    project_setup: "Project Setup", awaiting_client_brief: "Awaiting Client Brief", scheduled: "Scheduled",
    production: "Production", editing: "Editing", client_review: "Awaiting Client Review",
    balance_due: "Balance Due", ready_to_deliver: "Ready to Deliver", completed: "Completed",
    on_hold: "On Hold", cancelled: "Cancelled", archived: "Archived",
    // legacy aliases (old records still render a label until the server-side rename runs)
    awaiting_deposit: "Project Setup", pre_production: "Scheduled", shoot_scheduled: "Scheduled", production_completed: "Production",
    internal_review: "Editing", revisions: "Editing", delivered: "Completed",
};
export const ACTIVE_STAGES = ["project_setup", "awaiting_client_brief", "scheduled", "production", "editing",
    "client_review", "balance_due", "ready_to_deliver"];
export const STAGE_OPTIONS = [...ACTIVE_STAGES, "completed", "on_hold", "cancelled", "archived"];
export const PAY_LABELS = {
    no_payment_required: "No payment required", awaiting_deposit: "Awaiting deposit", deposit_paid: "Deposit paid",
    partially_paid: "Partially paid", balance_due: "Balance due", paid_in_full: "Paid in full", past_due: "Past due",
    refunded: "Refunded", partially_refunded: "Partially refunded", disputed: "Disputed",
};
export { API };

function Metric({ label, value, testid }) {
    return (
        <div data-testid={testid} className="bg-white rounded-xl border border-neutral-200 px-4 py-3">
            <div className="font-serif text-2xl text-cafe-espresso">{value}</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 mt-0.5">{label}</div>
        </div>
    );
}

export function ProjectCard({ c, onOpen }) {
    const payColor = c.payment_status === "paid_in_full" ? "text-cafe-olive" :
        ["awaiting_deposit", "past_due", "balance_due"].includes(c.payment_status) ? "text-red-600" : "text-cafe-terracotta";
    const overdue = c.due_date && c.due_date < new Date().toISOString().slice(0, 10) && ACTIVE_STAGES.includes(c.stage);
    return (
        <button data-testid={`project-card-${c.id}`} onClick={() => onOpen(c.id)}
            className={`bg-white rounded-xl border p-3 text-left w-full ${overdue ? "border-red-300" : "border-neutral-200"} hover:border-cafe-espresso/40`}>
            <div className="flex justify-between items-start gap-2">
                <span className="font-medium text-sm text-cafe-espresso">{c.brand}</span>
                <span className="text-[9px] text-neutral-400">{c.ref}</span>
            </div>
            <div className="text-[11px] text-neutral-500 mt-0.5">{c.package || "—"} · RM {Math.round(c.value).toLocaleString()}</div>
            <div className="text-[11px] mt-1"><span className={payColor}>● {PAY_LABELS[c.payment_status] || c.payment_status}</span>
                {c.deliverables.total > 0 && <span className="text-neutral-500"> · {c.deliverables.done}/{c.deliverables.total} deliverables</span>}
            </div>
            {c.shoot_date && <div className="text-[11px] text-neutral-500">Shoot: {c.shoot_date}</div>}
            <div className="text-xs mt-1 font-medium text-cafe-olive">→ {c.next_action || "Set next action"}</div>
            <div className={`text-[11px] ${overdue ? "text-red-600 font-semibold" : "text-neutral-500"}`}>
                {overdue ? `⚠ Overdue — ${c.due_date}` : c.due_date ? `Due ${c.due_date}` : "No due date"}
                {c.migration_review && <span className="text-amber-700 font-medium"> · ⚠ manual review</span>}
            </div>
        </button>
    );
}

export default function ProjectsOps() {
    const [view, setView] = useState("board");
    const [cls, setCls] = useState("live");
    const [metrics, setMetrics] = useState(null);
    const [data, setData] = useState(null);
    const [q, setQ] = useState("");
    const [filters, setFilters] = useState({ stage: "", payment: "" });
    const [openId, setOpenId] = useState(() => new URLSearchParams(window.location.search).get("project") || null);
    const [migInfo, setMigInfo] = useState(null);

    const load = useCallback(async () => {
        try {
            const params = new URLSearchParams({ cls, q, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) });
            const [m, b, mig] = await Promise.all([
                axios.get(`${API}/client-projects/metrics?cls=${cls}`),
                axios.get(`${API}/client-projects/board?${params}`),
                axios.get(`${API}/client-projects/migration/preview`),
            ]);
            setMetrics(m.data); setData(b.data); setMigInfo(mig.data);
        } catch { toast.error("Could not load projects"); }
    }, [cls, q, filters]);
    useEffect(() => { load(); }, [load]);

    const applyMigration = async () => {
        const { data: r } = await axios.post(`${API}/client-projects/migration/apply`);
        toast.success(`Migrated ${r.migrated} bookings (${r.manual_review_flagged} flagged for review)`);
        load();
    };

    return (
        <div data-testid="projects-ops" className="space-y-5">
            {migInfo?.to_migrate > 0 && (
                <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 text-sm flex flex-wrap justify-between items-center gap-2" data-testid="migration-banner">
                    <span>{migInfo.to_migrate} booking(s) not yet migrated to the project lifecycle.</span>
                    <button data-testid="migration-apply-btn" onClick={applyMigration}
                        className="text-xs px-3 py-1.5 rounded-full bg-cafe-espresso text-white">Migrate now (non-destructive)</button>
                </div>
            )}
            <div className="flex flex-wrap items-center gap-2 justify-between">
                <div className="flex rounded-full border border-neutral-300 overflow-hidden">
                    {[["board", LayoutGrid, "Board"], ["list", ListIcon, "List"], ["schedule", CalendarDays, "Schedule"]].map(([v, Icon, l]) => (
                        <button key={v} data-testid={`projects-view-${v}`} onClick={() => setView(v)}
                            className={`px-3 py-1.5 text-xs flex items-center gap-1 ${view === v ? "bg-cafe-espresso text-white" : "bg-white"}`}>
                            <Icon size={13} /> {l}
                        </button>
                    ))}
                </div>
                <select data-testid="projects-class-filter" value={cls} onChange={(e) => setCls(e.target.value)}
                    className="border border-neutral-300 rounded-full px-3 py-1.5 text-xs bg-white">
                    <option value="live">Live customers only</option>
                    <option value="all">Include demo/test</option>
                    <option value="internal">Internal</option>
                </select>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                <Metric testid="pm-active" label="Active projects" value={metrics?.active_projects ?? "—"} />
                <Metric testid="pm-deposit" label="Awaiting deposit" value={metrics?.awaiting_deposit ?? "—"} />
                <Metric testid="pm-shoots" label="Shoots next 7 days" value={metrics?.shoots_next_7_days ?? "—"} />
                <Metric testid="pm-review" label="Awaiting client review" value={metrics?.awaiting_client_review ?? "—"} />
                <Metric testid="pm-balance" label="Outstanding balances" value={`RM ${Math.round(metrics?.outstanding_balances || 0).toLocaleString()}`} />
            </div>
            <div className="flex flex-wrap gap-2 items-center">
                <div className="relative flex-1 min-w-[180px] max-w-xs">
                    <Search size={13} className="absolute left-2.5 top-2.5 text-neutral-400" />
                    <input data-testid="projects-search" value={q} onChange={(e) => setQ(e.target.value)}
                        placeholder="Search brand, ref, email, phone, location…"
                        className="w-full pl-8 pr-3 py-1.5 text-sm rounded-full border border-neutral-300 bg-white" />
                </div>
                <select data-testid="projects-filter-stage" value={filters.stage} onChange={(e) => setFilters({ ...filters, stage: e.target.value })}
                    className="border border-neutral-300 rounded-full px-2.5 py-1.5 text-xs bg-white">
                    <option value="">All stages</option>
                    {STAGE_OPTIONS.map((v) => <option key={v} value={v}>{STAGES[v]}</option>)}
                </select>
                <select data-testid="projects-filter-payment" value={filters.payment} onChange={(e) => setFilters({ ...filters, payment: e.target.value })}
                    className="border border-neutral-300 rounded-full px-2.5 py-1.5 text-xs bg-white">
                    <option value="">All payment statuses</option>
                    {Object.entries(PAY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
            </div>

            {!data ? <div className="text-sm text-neutral-500 py-10 text-center">Loading…</div> : (<>
                {view === "board" && (
                    <div className="space-y-3" data-testid="board-view">
                        {[...ACTIVE_STAGES, "completed"].map((s) => {
                            const cards = data.by_stage[s] || [];
                            if (!cards.length && !ACTIVE_STAGES.slice(0, 3).includes(s)) return null;
                            return (
                                <div key={s} className="bg-[#F5F0E6] rounded-xl p-3" data-testid={`pstage-${s}`}>
                                    <div className="text-xs font-semibold text-cafe-espresso uppercase tracking-[0.12em]">{STAGES[s]} · {cards.length}</div>
                                    {cards.length ? (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 mt-2">
                                            {cards.map((c) => <ProjectCard key={c.id} c={c} onOpen={setOpenId} />)}
                                        </div>
                                    ) : <div className="text-[11px] text-neutral-400 mt-1">Empty.</div>}
                                </div>
                            );
                        })}
                        {["on_hold", "cancelled", "archived"].some((s) => (data.by_stage[s] || []).length > 0) && (
                            <div className="bg-neutral-100 rounded-xl p-3">
                                <div className="text-xs font-semibold text-neutral-600 uppercase">On Hold / Cancelled / Archived</div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 mt-2">
                                    {["on_hold", "cancelled", "archived"].flatMap((s) => (data.by_stage[s] || [])).map((c) => <ProjectCard key={c.id} c={c} onOpen={setOpenId} />)}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {view === "list" && (
                    <div className="bg-white rounded-xl border border-neutral-200 divide-y" data-testid="list-view">
                        {!data.cards.length && <div className="text-sm text-neutral-500 py-8 text-center">No projects match these filters.</div>}
                        {data.cards.map((c) => (
                            <button key={c.id} data-testid={`plist-row-${c.id}`} onClick={() => setOpenId(c.id)}
                                className="w-full text-left grid grid-cols-2 md:grid-cols-[2fr,1.2fr,1fr,1fr,1.5fr,1fr] gap-2 px-4 py-2.5 text-xs hover:bg-neutral-50">
                                <span className="font-medium text-cafe-espresso">{c.brand}<span className="block text-[10px] text-neutral-400 font-normal">{c.ref} · {c.package}</span></span>
                                <span>{STAGES[c.stage]}</span>
                                <span>{PAY_LABELS[c.payment_status]}</span>
                                <span>RM {Math.round(c.value).toLocaleString()}</span>
                                <span className="text-cafe-olive">{c.next_action || "—"}</span>
                                <span className="text-neutral-500">{c.due_date || "—"}</span>
                            </button>
                        ))}
                    </div>
                )}
                {view === "schedule" && (
                    <div className="space-y-3" data-testid="schedule-view">
                        {!data.schedule.length && <div className="text-sm text-neutral-500 py-8 text-center bg-white rounded-xl border">No upcoming shoots scheduled.</div>}
                        {data.schedule.map((g) => (
                            <div key={g.date} className="bg-white rounded-xl border border-neutral-200 p-3">
                                <div className="text-xs font-semibold text-cafe-terracotta uppercase tracking-[0.12em]">{g.date}</div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
                                    {g.items.map((c) => <ProjectCard key={c.id} c={c} onOpen={setOpenId} />)}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </>)}
            {openId && <ProjectDetail projectId={openId} onClose={() => { setOpenId(null); load(); }} />}
        </div>
    );
}
